/* -------------------------- Design imports start -------------------------- */
// some design imports...
/* --------------------------- Design imports end --------------------------- */

import { Box, FormControlLabel, Grid, Stack, Switch, Typography } from "@mui/material";
import { SelectOption, SmartTableColumnType } from "../utils/types";
import MultilevelDrawer from "./layout/MultilevelDrawer"
import TextInput from "./inputs/TextInput"
import React, { useMemo, useRef, useState } from "react";
import NumberInput from "./inputs/NumberInput"
import DateInput from "./inputs/DateInput"
import Button from "./Button"
import { useTranslation } from "react-i18next"
import LogTool from "../logger/logTools"
import { GenericFilterType } from "../api/hooks/utils/types";



/* ------------------------ Functional imports start ------------------------ */
// some functional imports...
/* ------------------------- Functional imports end ------------------------- */
interface FilterGroup {
  key: string,
  label: string
}
interface FilterGroupMapping<T> extends FilterGroup {
  columns: SmartTableColumnType<T>[]
}

type Props<TEntity, TFilters> = {
  open: boolean,
  setOpen: (open: boolean) => void
  formState: {
    state: GenericFilterType,
    setState: React.Dispatch<React.SetStateAction<GenericFilterType>>
  }
  tableColumns: SmartTableColumnType<TEntity>[]
  onApplyFilter?: (selectedFilter: TFilters | undefined) => void
  onClose?: () => void
  searchLabel?: string
}



/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function TableFilterDrawer<TEntity, TFilters>(props: Props<TEntity, TFilters>) {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    formState,
    tableColumns,
    onApplyFilter = () => null,
    onClose = () => null,
    searchLabel,
  } = props
  const { t } = useTranslation()

  const log = new LogTool({context: 'TableFilterDrawer', enable: true, logLevel: 'debug'})
  /* --------------------------- Non state data end --------------------------- */



  /* ---------------------------- Flag states start --------------------------- */
  // some flag states...
  // e.g. const [openSupplierModal, setOpenSupplierModal] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */



  /* ---------------------------- Data states start --------------------------- */
  const mergedFilterColumns = useMemo(() => {
    return tableColumns.filter(clm => clm.filter)
  }, [tableColumns])

  const filterGroups: FilterGroupMapping<TEntity>[] = useMemo(() => {
    const uniqueFilterGroups: FilterGroup[] = getUniqueFilterGroups(mergedFilterColumns)
    log.debug('uniqueFilterGroups ->', uniqueFilterGroups)
    return uniqueFilterGroups.map((group: FilterGroup) => {
      return {
        key: group.key,
        label: group.label,
        columns: mergedFilterColumns.filter(clm => (
          clm.filter?.groupBy?.key === group.key
          && clm.filter?.groupBy?.label === group.label
        ))
      } as FilterGroupMapping<TEntity>
    })
  }, [mergedFilterColumns])
  const unGroupedFilterableColumns = useMemo(
    () => mergedFilterColumns.filter(clm => clm.filter && !clm.filter.groupBy),
    [mergedFilterColumns]
  )
  /* ----------------------------- Data states end ---------------------------- */



  /* ------------------------------ Effects start ----------------------------- */
  // some useEffects...
  // !! Note!!: you should try to avoid setting state in a useEffect as this renders
  // the component twice. For more info read: https://react.dev/learn/you-might-not-need-an-effect
  /* ------------------------------- Effects end ------------------------------ */



  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Returns an array of all unique filter groups defined through the filter.groupBy
   * property of the provided columns.
   * @param columns SmartTableColumnType[] - The column definitions to derrive the
 *                                           filter groups from.
   */
  function getUniqueFilterGroups(columns: SmartTableColumnType<TEntity>[]): FilterGroup[] {
    const groups = columns
      .filter(clm => clm.filter && clm.filter.groupBy)
      .map(clm => clm.filter.groupBy)
    if(groups.length === 0) return []
    return groups.filter(
      // remove duplicates
      (grp, index) => {
        const firstAppearance = groups.findIndex(g => g?.key === grp?.key)
        return firstAppearance === index
      }
    ) as FilterGroup[]
  }

  /**
   * Returns the key to use for API filters. Uses filterKey if specified,
   * otherwise falls back to dataIndex.
   */
  function getFilterKey(column: SmartTableColumnType<TEntity>): string {
    return column.filter?.filterKey ?? (column.dataIndex as string)
  }

  function parseNumericFilterValue(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed === '') return undefined
      const parsed = Number(trimmed.replace(',', '.'))
      return Number.isNaN(parsed) ? undefined : parsed
    }
    return undefined
  }

  function getNumberInputValue(value: unknown): string | number {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') return value
    return ''
  }
  /* -------------------------- Utility functions end ------------------------- */



  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false) => {
    // reset drawer
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleApplyFilter = () => {
    const cleanedFilters: GenericFilterType = {}
    for(const [property, value] of Object.entries(formState.state)) {
      log.debug('handling', property, 'with value', value)
      const columnForProperty = tableColumns.find(clm => getFilterKey(clm) === property)
      if (columnForProperty?.filter?.inputType === 'number') {
        const parsedValue = parseNumericFilterValue(value)
        if (parsedValue === undefined) continue
        cleanedFilters[property] = parsedValue
        continue
      }

      // cleanup the user input and only apply filters with meaningful values
      if(typeof value === 'string') {
        const cleanedValue = value.trim()
        if(cleanedValue === '') continue
        cleanedFilters[property] = cleanedValue
        continue
      }
      if (value !== undefined && value !== null) {
        cleanedFilters[property] = value
      }
    }

    // apply filters
    log.debug('applying filters ->', cleanedFilters)
    onApplyFilter(cleanedFilters as TFilters)

    handleClose(true)
  }

  const handleResetFilter = () => {
    formState.setState({})
    onApplyFilter(undefined)
    setOpen(false)
  }
  /* ------------------------- Callback functions end ------------------------- */



  /* ------------------------- Render constants start ------------------------- */
  const renderDrawerActions = () => {
    return(
      <Box sx={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
        <Stack direction='row' spacing={2}>
          <Button
            id="cancelButton"
            variant='contained'
            color='inherit'
            onClick={handleClose}
          >
            {t('common:interaction.button.cancel')}
          </Button>
          <Button
            id="resetButton"
            variant='outlined'
            color='primary'
            onClick={handleResetFilter}
          >
            {t('common:interaction.button.reset')}
          </Button>
        </Stack>
        <Button
          id="applyButton"
          variant='contained'
          onClick={handleApplyFilter}
        >
          {t('common:interaction.button.apply')}
        </Button>
      </Box>
    )
  }

  const renderColumnFilter = (col: SmartTableColumnType<TEntity>) => {
    return (
      <Box key={(col.key || col.dataIndex) as string}>
        {col.filter.inputType === 'text' && renderTextInput(col)}
        {col.filter.inputType === 'number' && renderNumberInput(col)}
        {/*{col.filter.inputType === 'measurement' && renderMeasurementInput(col)}*/}
        {col.filter.inputType === 'boolean' && renderBooleanInput(col)}
        {col.filter.inputType === 'date' && renderDateInput(col)}
        {col.filter.inputType === 'select' && renderSelectInput(col)}
      </Box>
    )
  }
  /**
   * Returns the ID to use for filter input elements. Uses column.key if available,
   * otherwise falls back to dataIndex.
   */
  const getFilterInputId = (column: SmartTableColumnType<TEntity>): string => {
    return (column.key as string) ?? (column.dataIndex as string)
  }

  const renderTextInput = (column: SmartTableColumnType<TEntity>) => {
    const filterKey = getFilterKey(column)
    const filterInputId = getFilterInputId(column)
    return (
      <TextInput
        id={`filter-${filterInputId}`}
        label={column.title as string}
        value={formState.state[filterKey] as string ?? ''}
        onChange={(value: string) => {
          formState.setState(prev => {
            prev[filterKey] = value
            return {...prev}
          })
        }}
      />
    )
  }

  const renderSelectInput = (column: SmartTableColumnType<TEntity>) => {
    // Fallback to a text input for select filters; keeps IDs compatible with Cypress commands
    const filterKey = getFilterKey(column)
    const filterInputId = getFilterInputId(column)
    return (
      <TextInput
        id={`filter-${filterInputId}-select`}
        label={column.title as string}
        value={formState.state[filterKey] as string ?? ''}
        onChange={(value: string) => {
          formState.setState(prev => {
            prev[filterKey] = value
            return {...prev}
          })
        }}
      />
    )
  }

  const renderNumberInput = (column: SmartTableColumnType<TEntity>) => {
    const filterKey = getFilterKey(column)
    const filterInputId = getFilterInputId(column)
    return (
      <NumberInput
        id={`filter-${filterInputId}`}
        label={column.title as string}
        value={getNumberInputValue(formState.state[filterKey])}
        onChange={(value: number | string) => {
          formState.setState(prev => {
            prev[filterKey] = String(value)
            return {...prev}
          })
        }}
      />
    )
  }

  const renderBooleanInput = (column: SmartTableColumnType<TEntity>) => {
    const filterKey = getFilterKey(column)
    const filterInputId = getFilterInputId(column)
    return (
      <FormControlLabel
        label={column.title as string}
        control={
          <Switch
            id={`filter-${filterInputId}-select`}
            name={formState.state.title as string}
            checked={formState.state[filterKey] as boolean ?? false}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              formState.setState(prev => {
                prev[filterKey] = event.target.checked
                return {...prev}
              })
            }}
          />
        }
      />
    )
  }

  const renderDateInput = (column: SmartTableColumnType<TEntity>) => {
    const filterKey = getFilterKey(column)
    const filterInputId = getFilterInputId(column)
    return (
      <DateInput
        id={`filter-${filterInputId}`}
        label={column.title as string}
        value={formState.state[filterKey] as string ?? ''}
        onChange={(value: string) => {
          formState.setState(prev => {
            prev[filterKey] = value
            return {...prev}
          })
        }}
      />
    )
  }
  /* -------------------------- Render constants end -------------------------- */



  /* ------------------------ Pre render actions start ------------------------ */
  // Effects that should happen on certain re-renders, that are not allowed in
  // useEffect because of setting states
  // e.g. setArticleOptions on the NewItemPage should be called every time
  // the modal is opened:
  //   if (open === true && prevOpen === false) {
  //     setPrevOpen(true)

  //     setArticleOptions(...)
  //   }
  /* ------------------------- Pre render actions end ------------------------- */


  log.debug(
    'formState.state ->', formState.state,
    ', columns ->', tableColumns,
    ', filterGroups ->', filterGroups,
  )

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      title={t('common:content.label.filter')}
      customActions={renderDrawerActions()}
    >
      <Typography variant='h6' mb={2}>{t('common:content.label.search')}</Typography>
      <TextInput
        id="generalSearchInput"
        label={searchLabel ?? t('common:content.label.search')}
        value={formState.state['q'] as string ?? ''}
        onChange={(value: string) => {
          formState.setState(prev => {
            prev['q'] = value
            return {...prev}
          })
        }}
      />
      {filterGroups && filterGroups.map(group => {
        return (
          <Box key={`${group.key}FilterGroup`}>
            <Typography variant='h6' mb={2} mt={4}>{group.label}</Typography>
            <Stack spacing={1}>
              {group.columns.map(renderColumnFilter)}
            </Stack>
          </Box>
        )
      })}
      {unGroupedFilterableColumns.length > 0 &&
        <Stack spacing={1}>
          {unGroupedFilterableColumns.map(renderColumnFilter)}
        </Stack>
      }
    </MultilevelDrawer>
  )
}
